import React, { useCallback, useEffect, useState } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  PlusOutlined,
  RobotOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Dropdown,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  type MenuProps,
  type TableProps,
} from "antd";
import isEqual from "lodash.isequal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";

import { programsActions, RequestStatus } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type TrainingProgramWithCreator } from "@fitness-app/data-models/entities/TrainingProgram";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useEntityChange } from "~/hooks/useEntityChange";
import { usePermission } from "~/hooks/usePermission";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { useTagOptions } from "~/hooks/useTagOptions";
import { useTeamMembers } from "~/modules/Team/hooks/useTeamMembers";
import ProgramForm from "~/modules/TrainingPrograms/components/ProgramForm/ProgramForm";
import { openTrainingProgramAgentDrawer } from "~/modules/TrainingPrograms/TrainingProgramAgent/trainingProgramAgentDrawerStore";
import { TRAINING_PROGRAM_AGENT_FEATURE_FLAG } from "~/modules/TrainingPrograms/TrainingProgramAgent/trainingProgramAgentFeatureFlag";
import { type ProgramFormModel } from "~/modules/TrainingPrograms/components/ProgramForm/types";
import { ProgramSearch } from "~/modules/TrainingPrograms/tabs/ProgramSearch";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

const ProgramsList = () => {
  const { t } = useTranslation(["workouts", "common"]);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const { listStatus, list, pagination, filters } = useAppSelector((store) => store.programs);
  const dispatch = useAppDispatch();
  const { filters: tagsFilters } = useTagOptions("trainingProgramTags");
  const [modal, contextHolder] = Modal.useModal();

  const loggedUser = useAppSelector((store) => store.user.data);
  const [editedModel, setEditedModel] = useState<(ProgramFormModel & { id: string }) | null>(null);
  const navigate = useNavigate();
  const { userId } = useUserRole();
  // `shared` decyduje, czy plan trafia do biblioteki całego zespołu, czy zostaje
  // prywatny. Domyślka szła po roli, więc head coach i administracja tworzyli
  // plany niewidoczne dla nikogo — łącznie z właścicielem konta.
  const sharesWithWholeTeam = usePermission(PermissionKey.PROGRAMS_MANAGE, PermissionScope.ALL);
  // Cudzy program rusza tylko ten, kto zarządza programami w całym koncie;
  // własny — jego autor. Wcześniej `isTrainer`, czyli wyłącznie ACCOUNT_OWNER.
  const canManageForeignPrograms = sharesWithWholeTeam;
  // Filtr „Dodane przez" ma sens od zakresu zespołu w górę.
  const canFilterByAuthor = usePermission(PermissionKey.PROGRAMS_MANAGE, PermissionScope.TEAM);
  const { createTrainerOptions } = useTeamMembers();
  const { enabled: isTrainingProgramAgentEnabled } = usePostHogFeatureFlag(TRAINING_PROGRAM_AGENT_FEATURE_FLAG);

  const [savingProgram, onSuccess, onFailure, onStart] = useEntityChange(() => {
    setShowProgramForm(false);
    setEditedModel(null);
  });

  const [duplicatingProgram, onSuccessDuplicated, onFailureDuplicated, onStartDuplicating] = useEntityChange();

  useEffect(() => {
    void dispatch(programsActions.fetchPrograms());
  }, [dispatch]);

  const toggleSharedState = async (programId: string, shared: boolean) => {
    await dispatch(
      programsActions.updateProgram({
        programId,
        program: {
          shared,
          updatedAt: new Date().toISOString(),
        },
      }),
    ).unwrap();
  };

  const onSubmitProgramForm = async (model: ProgramFormModel) => {
    onStart();
    try {
      if (editedModel) {
        await dispatch(
          programsActions.updateProgram({
            programId: editedModel.id,
            program: {
              ...model,
              updatedAt: new Date().toISOString(),
            },
          }),
        ).unwrap();
      } else {
        const programId = await dispatch(
          programsActions.addProgram({
            program: {
              ...model,
              shared: sharesWithWholeTeam,
              tags: model.tags || [],
              defaultSchedule: null,
              id: uuid(),
              createdBy: loggedUser?.id || null,
              updatedAt: new Date().toISOString(),
              createdAt: new Date().toISOString(),
              archived: false,
            },
          }),
        ).unwrap();

        navigate(programId);
      }

      onSuccess();
    } catch {
      onFailure();
    }
  };

  const duplicateProgram = async (program: TrainingProgramWithCreator) => {
    onStartDuplicating(program.id, t<string>("program.duplicating"));
    try {
      await dispatch(programsActions.duplicateProgram(program)).unwrap();
      onSuccessDuplicated();
    } catch {
      onFailureDuplicated();
    }
  };

  const getMenuItems = useCallback(
    (row: TrainingProgramWithCreator): MenuProps["items"] => [
      {
        label: t("common:button.duplicate"),
        key: "duplicate",
        icon: <CopyOutlined />,
        onClick: () => {
          void duplicateProgram(row);
        },
      },
      {
        label: t("common:button.edit"),
        key: "edit",
        icon: <EditOutlined />,
        onClick: () => {
          setEditedModel({
            name: row.name,
            level: row.level || null,
            type: row.type || null,
            comment: row.comment || "",
            id: row.id,
            tags: row.tags || [],
          });
          setShowProgramForm(true);
        },
      },
      {
        label: t("common:button.archive"),
        key: "delete",
        icon: <DeleteOutlined />,
        danger: true,

        onClick: () => {
          void modal.confirm({
            title: t("exercisesTable.deleteWarning"),
            icon: <ExclamationCircleOutlined />,
            okButtonProps: { danger: true },
            okText: t("common:button.archive"),
            onOk() {
              void dispatch(
                programsActions.updateProgram({
                  programId: row.id,
                  program: { archived: true },
                }),
              );
            },
          });
        },
        disabled: !canManageForeignPrograms && row.createdBy !== userId,
      },
    ],
    [t, canManageForeignPrograms, userId],
  );

  const handleChange: TableProps<TrainingProgramWithCreator>["onChange"] = (tablePagination, tableFilters) => {
    if (!canFilterByAuthor) {
      if (!isEqual(filters.tags, tableFilters.tags)) {
        void dispatch(
          programsActions.fetchPrograms({
            tags: tableFilters.tags as string[],
            page: 1,
          }),
        );
      } else if (tablePagination.current && tablePagination.current !== pagination.currentPage) {
        void dispatch(programsActions.fetchPrograms({ page: tablePagination.current }));
        return;
      }
    } else {
      if (!isEqual(filters.addedBy ? [filters.addedBy] : null, tableFilters.createdBy)) {
        const newTrainerFilter = tableFilters.createdBy?.[0] || null;
        void dispatch(
          programsActions.fetchPrograms({
            addedBy: (newTrainerFilter as string) || null,
            page: 1,
          }),
        );
      } else if (!isEqual(filters.tags, tableFilters.tags)) {
        void dispatch(
          programsActions.fetchPrograms({
            tags: tableFilters.tags as string[],
            addedBy: filters.addedBy || null,
            page: 1,
          }),
        );
      } else if (tablePagination.current && tablePagination.current !== pagination.currentPage) {
        void dispatch(programsActions.fetchPrograms({ page: tablePagination.current }));
        return;
      }
    }
  };

  return (
    <Card
      extra={
        <Space>
          {isTrainingProgramAgentEnabled ? (
            <Button icon={<RobotOutlined />} onClick={openTrainingProgramAgentDrawer}>
              Utwórz z AI
            </Button>
          ) : null}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowProgramForm(true)}>
            {t("program.addNewProgram")}
          </Button>
        </Space>
      }
    >
      <Space size={12} wrap>
        <ProgramSearch />
        <Space className="my-4 whitespace-nowrap">
          <Switch
            checked={filters.addedBy === userId}
            onChange={(active) => {
              void dispatch(
                programsActions.fetchPrograms({
                  addedBy: active ? userId : null,
                  page: 1,
                }),
              );
            }}
          />
          <Typography.Text>Wyświetl tylko moje plany</Typography.Text>
        </Space>
      </Space>

      <Table
        dataSource={list}
        loading={listStatus === RequestStatus.FETCHING}
        pagination={{
          pageSize: pagination.perPage,
          total: pagination.totalPages * pagination.perPage,
          current: pagination.currentPage,
          showSizeChanger: false,
        }}
        rowKey="id"
        scroll={{ x: true }}
        onChange={handleChange}
      >
        <Table.Column<TrainingProgramWithCreator> title={t("program.name")} dataIndex="name" key="name" />
        <Table.Column<TrainingProgramWithCreator>
          title={t("program.type")}
          dataIndex="type"
          key="type"
          responsive={["md"]}
          render={(type) => t(`programType.${type}`)}
          // filters={programTypeOptions.map((part) => ({
          //   text: part.label,
          //   value: part.value,
          // }))}
          // onFilter={(value, record) => (record.type ? record.type.includes(value as TrainingProgramTypeEnum) : false)}
        />
        <Table.Column<TrainingProgramWithCreator>
          title={t("program.level")}
          dataIndex="level"
          key="level"
          responsive={["lg"]}
          render={(level) => t(`programLevel.${level}`)}
          // filters={programLevelOptions.map((part) => ({
          //   text: part.label,
          //   value: part.value,
          // }))}
          // onFilter={(value, record) =>
          //   record.level ? record.level.includes(value as TrainingProgramLevelEnum) : false
          // }
        />
        <Table.Column<TrainingProgramWithCreator>
          title={t("program.tags")}
          dataIndex="tags"
          key="tags"
          responsive={["xl"]}
          filterMultiple
          filterSearch
          filters={tagsFilters}
          filteredValue={filters.tags}
          render={(tags: string[]) =>
            tags?.length ? (
              <Space direction="vertical">
                {tags.map((tag, i) => (
                  <Tag key={`${tag}-${i}`}>{tag}</Tag>
                ))}
              </Space>
            ) : (
              "-"
            )
          }
        />

        <Table.Column<TrainingProgramWithCreator>
          title={t("program.createdBy")}
          dataIndex="createdBy"
          key="createdBy"
          responsive={["lg"]}
          {...(!canFilterByAuthor
            ? {}
            : {
                filterMultiple: false,
                filterSearch: true,
                filteredValue: filters.addedBy ? [filters.addedBy] : null,
                filters: createTrainerOptions(true),
              })}
          render={(_createdBy: string, row) => {
            if (row.creator) {
              return `${row.creator.firstName} ${row.creator.lastName}`;
            }
            return "-";
          }}
        />
        <Table.Column<TrainingProgramWithCreator>
          title={t("program.shared")}
          dataIndex="shared"
          key="shared"
          responsive={["lg"]}
          render={(shared: boolean, row) => {
            return (
              <Switch
                unCheckedChildren={<EyeInvisibleOutlined />}
                checkedChildren={<ShareAltOutlined />}
                disabled={!canManageForeignPrograms && row.createdBy !== userId}
                defaultChecked={shared}
                onChange={(change) => {
                  void toggleSharedState(row.id, change);
                }}
              />
            );
          }}
        />
        <Table.Column<TrainingProgramWithCreator>
          title={t("program.options")}
          key="action"
          align="left"
          render={(_, row) => (
            <>
              <Dropdown.Button
                menu={{ items: getMenuItems(row) }}
                loading={duplicatingProgram === row.id}
                onClick={() => {
                  navigate(row.id);
                }}
              >
                {t("common:button.details")}
              </Dropdown.Button>
              {contextHolder}
            </>
          )}
        />
      </Table>
      <ModalForm
        title={editedModel ? t("program.editProgram") : t("program.addNewProgram")}
        loading={!!savingProgram}
        open={showProgramForm}
        onCancel={() => {
          setShowProgramForm(false);
          setEditedModel(null);
        }}
      >
        <ProgramForm onSubmit={onSubmitProgramForm} model={editedModel} />
      </ModalForm>
    </Card>
  );
};

export default ProgramsList;
